import { ReactElement, useState, useRef, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Typography, Paper, CircularProgress } from '@mui/material'
import MuxPlayer from '@mux/mux-player-react'
import { TranscriptEditor } from 'src/v3/components/transcript-editor/transcript-editor'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import { selectUserID } from 'src/v3/settings/settings.slice'
import { useVttSubtitles } from 'src/v3/utilities/hooks/use-vtt-subtitles'
import {
  fetchVideos,
  selectVideoByID,
  selectVideosStatus,
} from '../create.slice'

export default (): ReactElement => {
  let { id } = useParams<{ id: string }>()
  let dispatch = useAppDispatch()
  let userID = useAppSelector(selectUserID)
  let videosStatus = useAppSelector(selectVideosStatus)
  let video = useAppSelector(selectVideoByID(id || ''))

  let [currentTime, setCurrentTime] = useState(0)
  let playerRef = useRef(null)

  // Load VTT subtitles if available
  let { vttUrl } = useVttSubtitles(id)

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus, dispatch])

  // Function to seek to a specific time in the video
  let handleSeek = useCallback(
    (time: number) => {
      if (playerRef.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - MuxPlayer API might not be fully typed
        // eslint-disable-next-line fp/no-mutation
        playerRef.current.currentTime = time
      }
    },
    [playerRef],
  )

  // Handle time updates from the player
  let handleTimeUpdate = (event: any) => {
    let player = event.target
    setCurrentTime(player.currentTime)
  }

  if (videosStatus === 'pending' || videosStatus === 'uninitialized') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!video) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Video not found</Typography>
      </Box>
    )
  }

  return (
    <Box
      component="main"
      sx={{
        p: 3,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        {video.name}
      </Typography>

      {/* Video Player */}
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Box sx={{ height: '400px', maxHeight: '400px' }}>
          <MuxPlayer
            ref={playerRef}
            title={video.name}
            src={`${video?.fileName.replace('mp4', 'm3u8')}`}
            accentColor="#f9ea4f"
            primaryColor="#6694E9"
            backwardSeekOffset={10}
            forwardSeekOffset={10}
            metadata={{
              video_id: video.id,
              video_title: video.name,
              viewer_user_id: userID,
            }}
            onTimeUpdate={handleTimeUpdate}
            defaultHiddenCaptions={!vttUrl} // Only hide captions if we don't have a VTT file
            streamType="on-demand"
            style={{ height: '100%', width: '100%', maxHeight: '400px' }}
          >
            {vttUrl && (
              <track
                src={vttUrl}
                kind="subtitles"
                label="English"
                srcLang="en"
                default
              />
            )}
          </MuxPlayer>
        </Box>
      </Paper>

      {/* Transcript Editor */}
      <Box
        sx={{ flexGrow: 1, height: 'calc(100vh - 550px)', minHeight: '300px' }}
      >
        <TranscriptEditor
          currentTime={currentTime}
          onSeek={handleSeek}
          videoId={id}
        />
      </Box>
    </Box>
  )
}
